// ViewFields.js
// Thin wrapper/facade around Fields.
// - No dictionaries.
// - No external asset wiring (AssetRegistry is used internally via FieldOptions).
// - Single source of truth for processed field definitions per view.

import Fields, { resolveOptionsForField } from "./Fields.js";

let _instance = null;

function createInstance() {
  let _handlers = {
    onCellUpdate: null,
    onFileUpdate: null,
    onReviewerChange: null,
    onOpenAiReviewPanel: null,
  };

  let _uiContext = {
    theme: null,
    locale: null,
    projectConfig: null,
  };

  let _processedFields = null;

  function setProcessedFields(fields) {
    _processedFields = Array.isArray(fields) ? fields : null;
  }

  function getProcessedFields({ requireBuilt = true } = {}) {
    if (!_processedFields) {
      if (requireBuilt) {
        throw new Error(
          "[ViewFields.getProcessedFields] No processed fields are available. " +
            "Call buildProcessedFields() or buildFieldsSync() first."
        );
      }
      return null;
    }
    return _processedFields;
  }

  return {
    setHandlers(h = {}) {
      const next = { ..._handlers };

      if (typeof h.onCellUpdate === "function") next.onCellUpdate = h.onCellUpdate;
      if (typeof h.onFileUpdate === "function") next.onFileUpdate = h.onFileUpdate;
      if (typeof h.onReviewerChange === "function") next.onReviewerChange = h.onReviewerChange;
      if (typeof h.onOpenAiReviewPanel === "function") next.onOpenAiReviewPanel = h.onOpenAiReviewPanel;

      _handlers = next;
    },

    getHandlers() {
      return _handlers;
    },

    setUIContext({ theme, locale, projectConfig } = {}) {
      if (theme !== undefined) {
        _uiContext.theme = theme;
      }
      if (locale !== undefined) {
        _uiContext.locale = locale;
      }
      if (projectConfig !== undefined) {
        _uiContext.projectConfig = projectConfig;
      }
    },

    getUIContext() {
      return { ..._uiContext };
    },

    resolveOptionsForFieldExternal(fieldConfig) {
      return resolveOptionsForField(fieldConfig);
    },

    async buildFieldsSync({ fields, projectCode, getOnlyVisible, filterType, projectConfig } = {}) {
      if (!Array.isArray(fields)) {
        throw new Error("[ViewFields.buildFieldsSync] 'fields' must be an array.");
      }

      await this.buildProcessedFields({
        fieldDefinitions: fields,
        projectCode,
        getOnlyVisible,
        filterType,
        projectConfig,
      });

      return true;
    },

    async buildProcessedFields({
      fieldDefinitions,
      projectCode,
      getOnlyVisible,
      filterType,
      projectConfig,
    } = {}) {
      if (!Array.isArray(fieldDefinitions)) {
        throw new Error(
          "[ViewFields.buildProcessedFields] 'fieldDefinitions' must be an array."
        );
      }

      const { theme, locale, projectConfig: contextProjectConfig } = this.getUIContext();
      const {
        onCellUpdate,
        onFileUpdate,
        onReviewerChange,
        onOpenAiReviewPanel,
      } = this.getHandlers();

      const processed = await Fields(
        fieldDefinitions,
        projectCode,
        getOnlyVisible,
        filterType,
        onCellUpdate,
        onFileUpdate,
        onReviewerChange,
        onOpenAiReviewPanel,
        theme,
        locale,
        projectConfig !== undefined ? projectConfig : contextProjectConfig
      );

      setProcessedFields(processed);
    },

    getProcessedFields,

    clearProcessedFieldsCache() {
      // Intentionally do nothing.
    },
  };
}

export function initViewFields({ handlers, theme, locale, projectConfig } = {}) {
  const root =
    (typeof window !== "undefined" && window) ||
    (typeof global !== "undefined" && global) ||
    {};

  if (!_instance) {
    _instance = root.__VF_SINGLETON__ || createInstance();
    root.__VF_SINGLETON__ = _instance;
  }

  if (handlers) {
    _instance.setHandlers(handlers);
  }

  _instance.setUIContext({ theme, locale, projectConfig });

  return _instance;
}

const ViewFields = { initViewFields };
export default ViewFields;
